import React, { useEffect, useState, useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, PlusIcon, SendIcon, ShieldCheckIcon, FileTextIcon, CalculatorIcon, AlertCircleIcon, GraduationCapIcon, CarIcon, SmartphoneIcon, ChevronRightIcon, WalletIcon } from 'lucide-react';
import { formatNaira } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface DashboardProps {
  navigate: (screen: string, productId?: string) => void;
  viewMode?: 'app' | 'tablet';
}
export function Dashboard({
  navigate,
  viewMode = 'app'
}: DashboardProps) {
  const [activeWalletCard, setActiveWalletCard] = useState(0);
  const [kycStatus, setKycStatus] = useState<'pending' | 'completed' | 'hidden'>('pending');
  const walletScrollRef = useRef<HTMLDivElement>(null);
  const walletBalance = 2450000;
  const totalCoverage = 35000000;
  const protectionScore = 85;
  useEffect(() => {
    const status = sessionStorage.getItem('kyc_status');
    if (status === 'completed') {
      setKycStatus('completed');
      sessionStorage.setItem('kyc_status', 'hidden');
    } else if (status === 'hidden') {
      setKycStatus('hidden');
    }
  }, []);
  const handleWalletScroll = () => {
    if (walletScrollRef.current) {
      const scrollLeft = walletScrollRef.current.scrollLeft;
      const cardWidth = walletScrollRef.current.offsetWidth;
      const index = Math.round(scrollLeft / cardWidth);
      setActiveWalletCard(index);
    }
  };
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const scrollStartX = useRef(0);
  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    scrollStartX.current = walletScrollRef.current?.scrollLeft || 0;
    if (walletScrollRef.current) {
      walletScrollRef.current.style.scrollSnapType = 'none';
    }
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !walletScrollRef.current) return;
    e.preventDefault();
    const diff = dragStartX.current - e.clientX;
    walletScrollRef.current.scrollLeft = scrollStartX.current + diff;
  };
  const handleMouseUp = () => {
    if (!isDragging.current || !walletScrollRef.current) return;
    isDragging.current = false;
    walletScrollRef.current.style.scrollSnapType = 'x mandatory';
    const cardWidth = walletScrollRef.current.offsetWidth;
    const index = Math.round(walletScrollRef.current.scrollLeft / cardWidth);
    walletScrollRef.current.scrollTo({
      left: index * cardWidth,
      behavior: 'smooth'
    });
    setActiveWalletCard(index);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  const renderProtectionCard = () => <>
      <div className="absolute top-0 right-0 w-48 h-48 bg-white/40 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none" data-id="element-1486"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#2BA89D]/20 rounded-full blur-3xl -ml-10 -mb-10 pointer-events-none" data-id="element-1487"></div>

      <div className="relative z-10 flex flex-col h-full" data-id="element-1488">
        <div className="flex justify-between items-start mb-6" data-id="element-1489">
          <div data-id="element-1490">
            <p className="text-[#0D3B3B]/70 text-xs font-bold uppercase tracking-widest mb-1" data-id="element-1491">
              Your Protection
            </p>
            <div className="inline-flex items-center gap-1 bg-white/40 backdrop-blur-md px-2 py-1 rounded-md text-[10px] font-bold" data-id="element-1492">
              <ShieldCheckIcon className="w-3 h-3 text-[#2BA89D]" data-id="element-1493" /> Fully
              Covered
            </div>
          </div>
          <div className="w-10 h-10 bg-white/40 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 p-2.5" data-id="element-1494">
            <img src="https://cdn.magicpatterns.com/uploads/cwrSk7LKtqWDafoVbnwsDs/Group_575.png" alt="DD" className="w-full h-full object-contain opacity-90" data-id="element-1495" />
          </div>
        </div>

        <div className="mb-6" data-id="element-1496">
          <p className="text-[#0D3B3B]/70 text-xs font-medium mb-1" data-id="element-1497">
            Total Coverage
          </p>
          <h2 className="text-4xl font-black tracking-tight" data-id="element-1498">
            {formatNaira(totalCoverage)}
          </h2>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-6 bg-white/30 rounded-2xl p-3 backdrop-blur-sm border border-white/20" data-id="element-1499">
          <div className="text-center border-r border-[#0D3B3B]/10" data-id="element-1500">
            <p className="text-[10px] text-[#0D3B3B]/60 uppercase tracking-wider font-bold mb-0.5" data-id="element-1501">
              Active
            </p>
            <p className="text-sm font-bold" data-id="element-1502">4 Plans</p>
          </div>
          <div className="text-center border-r border-[#0D3B3B]/10" data-id="element-1503">
            <p className="text-[10px] text-[#0D3B3B]/60 uppercase tracking-wider font-bold mb-0.5" data-id="element-1504">
              Saved
            </p>
            <p className="text-sm font-bold text-[#2BA89D]" data-id="element-1505">₦4.2M</p>
          </div>
          <div className="text-center" data-id="element-1506">
            <p className="text-[10px] text-[#0D3B3B]/60 uppercase tracking-wider font-bold mb-0.5" data-id="element-1507">
              Score
            </p>
            <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-1508">
              {protectionScore}%
            </p>
          </div>
        </div>

        <button onClick={() => navigate('products')} className="w-full mt-auto bg-[#0D3B3B] hover:bg-[#092D2D] text-[#FFD700] font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-[#0D3B3B]/20" data-id="element-1509">
          Explore Products <ArrowRightIcon className="w-4 h-4" data-id="element-1510" />
        </button>
      </div>
    </>;
  const renderWalletCard = () => <>
      <div className="absolute top-0 right-0 w-48 h-48 bg-white/40 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none" data-id="element-1511" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#2BA89D]/20 rounded-full blur-3xl -ml-10 -mb-10 pointer-events-none" data-id="element-1512" />

      <div className="relative z-10 flex flex-col h-full" data-id="element-1513">
        <div className="flex justify-between items-start mb-6" data-id="element-1514">
          <div data-id="element-1515">
            <p className="text-[#0D3B3B]/70 text-xs font-bold uppercase tracking-widest mb-1" data-id="element-1516">
              Skydd Wallet
            </p>
            <div className="inline-flex items-center gap-1 bg-white/40 backdrop-blur-md px-2 py-1 rounded-md text-[10px] font-bold" data-id="element-1517">
              <WalletIcon className="w-3 h-3 text-[#2BA89D]" data-id="element-1518" /> Active
            </div>
          </div>
          <div className="w-10 h-10 bg-white/40 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 p-2.5" data-id="element-1519">
            <img src="https://cdn.magicpatterns.com/uploads/dswE38yhcdHjQ4yjGk39Ep/Group_574.png" alt="DD" className="w-full h-full object-contain opacity-90" data-id="element-1520" />
          </div>
        </div>

        <div className="mb-6" data-id="element-1521">
          <p className="text-[#0D3B3B]/70 text-xs font-medium mb-1" data-id="element-1522">
            Available Balance
          </p>
          <h2 className="text-4xl font-black tracking-tight" data-id="element-1523">
            {formatNaira(walletBalance)}
          </h2>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-6 bg-white/30 rounded-2xl p-3 backdrop-blur-sm border border-white/20" data-id="element-1524">
          <div className="text-center border-r border-[#0D3B3B]/10" data-id="element-1525">
            <p className="text-[10px] text-[#0D3B3B]/60 uppercase tracking-wider font-bold mb-0.5" data-id="element-1526">
              This Month
            </p>
            <p className="text-sm font-bold text-[#2BA89D]" data-id="element-1527">₦1.8M</p>
          </div>
          <div className="text-center border-r border-[#0D3B3B]/10" data-id="element-1528">
            <p className="text-[10px] text-[#0D3B3B]/60 uppercase tracking-wider font-bold mb-0.5" data-id="element-1529">
              Pending
            </p>
            <p className="text-sm font-bold" data-id="element-1530">₦0</p>
          </div>
          <div className="text-center" data-id="element-1531">
            <p className="text-[10px] text-[#0D3B3B]/60 uppercase tracking-wider font-bold mb-0.5" data-id="element-1532">
              Txns
            </p>
            <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-1533">12</p>
          </div>
        </div>

        <div className="flex gap-3 mt-auto" data-id="element-1534">
          <button onClick={e => {
          e.stopPropagation();
          navigate('fund-wallet');
        }} className="flex-1 bg-[#0D3B3B] hover:bg-[#092D2D] text-[#FFD700] font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-[#0D3B3B]/20 text-sm" data-id="element-1535">
            <PlusIcon className="w-4 h-4" data-id="element-1536" /> Fund
          </button>
          <button onClick={e => {
          e.stopPropagation();
          navigate('transfer');
        }} className="flex-1 bg-white/50 hover:bg-white/70 text-[#0D3B3B] font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors border border-white/40 text-sm backdrop-blur-sm active:scale-95" data-id="element-1537">
            <SendIcon className="w-4 h-4" data-id="element-1538" /> Transfer
          </button>
        </div>
      </div>
    </>;
  const renderPlansList = () => <>
      <div className="flex justify-between items-center mb-4 px-1" data-id="element-1539">
        <h2 className="text-lg font-black text-[#0D3B3B] tracking-tight" data-id="element-1540">
          My Plans
        </h2>
        <button onClick={() => navigate('policies')} className="text-xs font-bold text-[#2BA89D] hover:text-[#1e7a72] transition-colors flex items-center gap-0.5" data-id="element-1541">
          View All <ChevronRightIcon className="w-4 h-4" data-id="element-1542" />
        </button>
      </div>

      <div className="space-y-4" data-id="element-1543">
        {/* Plan 1 */}
        <div onClick={() => navigate('policies')} className="bg-white rounded-[1.5rem] p-5 border border-[#0D3B3B]/5 shadow-sm cursor-pointer hover:shadow-md hover:border-[#2BA89D]/20 transition-all active:scale-[0.98] group" data-id="element-1544">
          <div className="flex items-start gap-4 mb-4" data-id="element-1545">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0 group-hover:scale-110 transition-transform duration-300" data-id="element-1546">
              <GraduationCapIcon className="w-6 h-6" data-id="element-1547" />
            </div>
            <div className="flex-1" data-id="element-1548">
              <h3 className="font-bold text-[#0D3B3B] text-base group-hover:text-[#2BA89D] transition-colors" data-id="element-1549">
                Future Scholar Fund
              </h3>
              <p className="text-xs text-gray-500 font-medium" data-id="element-1550">
                Leadway Assurance
              </p>
            </div>
            <div className="text-right" data-id="element-1551">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5" data-id="element-1552">
                Coverage
              </p>
              <p className="text-sm font-black text-[#0D3B3B]" data-id="element-1553">₦10.0M</p>
            </div>
          </div>

          <div className="mb-4" data-id="element-1554">
            <div className="flex justify-between text-xs mb-1.5" data-id="element-1555">
              <span className="font-medium text-gray-500" data-id="element-1556">
                Savings Progress
              </span>
              <span className="font-bold text-emerald-600" data-id="element-1557">42%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-1.5" data-id="element-1558">
              <div className="h-full bg-emerald-500 rounded-full" style={{
              width: '42%'
            }} data-id="element-1559"></div>
            </div>
            <p className="text-[10px] text-gray-400 font-medium" data-id="element-1560">
              ₦4,200,000 saved of ₦10,000,000
            </p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-50" data-id="element-1561">
            <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wider" data-id="element-1562">
              Active
            </span>
            <div className="text-right" data-id="element-1563">
              <span className="text-sm font-black text-[#0D3B3B]" data-id="element-1564">₦16,666</span>
              <span className="text-[10px] text-gray-500 font-medium" data-id="element-1565">/mo</span>
            </div>
          </div>
        </div>

        {/* Plan 2 */}
        <div onClick={() => navigate('policies')} className="bg-white rounded-[1.5rem] p-5 border border-[#0D3B3B]/5 shadow-sm cursor-pointer hover:shadow-md hover:border-[#2BA89D]/20 transition-all active:scale-[0.98] group" data-id="element-1566">
          <div className="flex items-start gap-4 mb-4" data-id="element-1567">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0 group-hover:scale-110 transition-transform duration-300" data-id="element-1568">
              <CarIcon className="w-6 h-6" data-id="element-1569" />
            </div>
            <div className="flex-1" data-id="element-1570">
              <h3 className="font-bold text-[#0D3B3B] text-base group-hover:text-[#2BA89D] transition-colors" data-id="element-1571">
                AutoGuard (Comp)
              </h3>
              <p className="text-xs text-gray-500 font-medium" data-id="element-1572">AXA Mansard</p>
            </div>
            <div className="text-right" data-id="element-1573">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5" data-id="element-1574">
                Coverage
              </p>
              <p className="text-sm font-black text-[#0D3B3B]" data-id="element-1575">₦5.0M</p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-50" data-id="element-1576">
            <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wider" data-id="element-1577">
              Active
            </span>
            <div className="text-right" data-id="element-1578">
              <span className="text-sm font-black text-[#0D3B3B]" data-id="element-1579">
                ₦150,000
              </span>
              <span className="text-[10px] text-gray-500 font-medium" data-id="element-1580">/yr</span>
            </div>
          </div>
        </div>
      </div>
    </>;
  const renderQuickActions = () => <>
      <h3 className="text-lg font-black text-[#0D3B3B] tracking-tight mb-4 px-1" data-id="element-1581">
        Quick Actions
      </h3>
      <div className="grid grid-cols-2 gap-3" data-id="element-1582">
        {[{
        icon: ShieldCheckIcon,
        label: 'Get Covered',
        desc: 'Browse products',
        color: 'text-[#2BA89D]',
        bg: 'bg-[#2BA89D]/10',
        action: () => navigate('products')
      }, {
        icon: AlertCircleIcon,
        label: 'Make Claim',
        desc: 'Report incident',
        color: 'text-rose-500',
        bg: 'bg-rose-50',
        action: () => navigate('claims')
      }, {
        icon: CalculatorIcon,
        label: 'Calculator',
        desc: 'Plan savings',
        color: 'text-[#FFD700]',
        bg: 'bg-[#FFD700]/20',
        action: () => navigate('product-detail', 'goalguard')
      }, {
        icon: FileTextIcon,
        label: 'Documents',
        desc: 'View certs',
        color: 'text-indigo-500',
        bg: 'bg-indigo-50',
        action: () => navigate('policies')
      }].map((item, idx) => <button key={idx} onClick={item.action} className="bg-white border border-[#0D3B3B]/5 p-4 rounded-[1.5rem] flex flex-col items-start gap-3 shadow-sm hover:shadow-md hover:border-[#2BA89D]/20 transition-all active:scale-95 group" data-id="element-1583">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${item.bg}`} data-id="element-1584">
              <item.icon className={`w-5 h-5 ${item.color}`} data-id="element-1585" />
            </div>
            <div className="text-left" data-id="element-1586">
              <span className="block text-sm font-bold text-[#0D3B3B] group-hover:text-[#2BA89D] transition-colors" data-id="element-1587">
                {item.label}
              </span>
              <span className="block text-[10px] font-medium text-gray-500 mt-0.5" data-id="element-1588">
                {item.desc}
              </span>
            </div>
          </button>)}
      </div>
    </>;
  const renderAIBanner = () => <>
      <div className="absolute top-0 right-0 w-32 h-32 bg-[#2BA89D]/20 rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none" data-id="element-1589"></div>
      <div className="relative z-10 flex items-center gap-4" data-id="element-1590">
        <div className="w-12 h-12 rounded-full bg-[#FFD700]/20 flex items-center justify-center shrink-0 border border-[#FFD700]/30 p-1" data-id="element-1591">
          <img src="https://cdn.magicpatterns.com/uploads/twohoUfqTs4sUfnNQ4Jbt5/DD_with_umbrella_1.png" alt="Mascot" className="w-10 h-10 object-contain drop-shadow-md group-hover:scale-110 transition-transform" data-id="element-1592" />
        </div>
        <div data-id="element-1593">
          <h3 className="font-bold text-sm mb-0.5 text-[#FFD700]" data-id="element-1594">
            Insurance 101
          </h3>
          <p className="text-xs text-white/80 font-medium" data-id="element-1595">
            Learn how claims work in 2 mins
          </p>
        </div>
      </div>
      <button className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm group-hover:bg-[#FFD700] group-hover:text-[#0D3B3B] transition-colors relative z-10 shrink-0" data-id="element-1596">
        <ArrowRightIcon className="w-4 h-4" data-id="element-1597" />
      </button>
    </>;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className={`flex-1 overflow-y-auto ${viewMode === 'app' ? 'pb-24' : 'pb-6'} bg-slate-50`} data-id="element-1598">
      <TopBar onNotificationClick={() => navigate('notifications')} onProfileClick={() => navigate('profile')} data-id="element-1599" />

      <div className={`px-5 pt-2 pb-6 ${viewMode === 'tablet' ? 'max-w-6xl mx-auto' : ''}`} data-id="element-1600">
        {/* Greeting */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} className="mb-6" data-id="element-1601">
          <h1 className="text-2xl font-black text-[#0D3B3B] flex items-center gap-2" data-id="element-1602">
            Good morning, Adaeze
            <motion.img src="https://cdn.magicpatterns.com/uploads/vJQZF1GRsZggegtRVotgXV/DD_waving_hero_1.png" alt="Skydd mascot" animate={{
            rotate: [0, 8, -4, 8, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 3
          }} className="inline-block w-12 h-12 object-contain origin-bottom-right -mb-1 drop-shadow-md" data-id="element-1603" />
          </h1>
          <p className="text-sm text-gray-500 font-medium" data-id="element-1604">
            Your protection is looking great!
          </p>
        </motion.div>

        {/* KYC Banner */}
        {kycStatus !== 'hidden' && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} onClick={() => kycStatus === 'pending' && navigate('kyc')} className={`rounded-[1.5rem] p-5 text-white mb-6 shadow-lg transition-all relative overflow-hidden ${kycStatus === 'completed' ? 'bg-[#0D3B3B] shadow-[#0D3B3B]/20' : 'bg-gradient-to-r from-emerald-500 to-emerald-600 shadow-emerald-500/20 cursor-pointer active:scale-95'}`} data-id="element-1605">
            <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-1606"></div>
            <div className="flex justify-between items-center relative z-10" data-id="element-1607">
              {kycStatus === 'pending' ? <>
                  <div data-id="element-1608">
                    <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold mb-2 border border-white/20" data-id="element-1609">
                      <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-1610" /> Tier 2
                      Verified
                    </div>
                    <p className="text-sm text-emerald-50 font-medium" data-id="element-1611">
                      BVN ✓ • ID ✓ • Address ⏳
                    </p>
                  </div>
                  <div className="text-right" data-id="element-1612">
                    <span className="text-xs font-bold bg-white text-emerald-600 px-3 py-1.5 rounded-lg shadow-sm" data-id="element-1613">
                      Upgrade
                    </span>
                  </div>
                </> : <>
                  <div data-id="element-1614">
                    <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold mb-2 border border-white/20" data-id="element-1615">
                      <ShieldCheckIcon className="w-3.5 h-3.5 text-[#FFD700]" data-id="element-1616" />{' '}
                      Fully Verified
                    </div>
                    <p className="text-sm text-white/80 font-medium" data-id="element-1617">
                      All verification steps complete ✓
                    </p>
                  </div>
                  <div className="text-right" data-id="element-1618">
                    <span className="text-xs font-bold bg-[#FFD700] text-[#0D3B3B] px-3 py-1.5 rounded-lg shadow-sm" data-id="element-1619">
                      Completed
                    </span>
                  </div>
                </>}
            </div>
          </motion.div>}

        {/* Hero Cards - Different layout for Web vs Mobile */}
        {viewMode === 'tablet' /* WEB LAYOUT: Strict 2-column grid, no swiping */ ? <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="grid grid-cols-2 gap-6 mb-8" data-id="element-1620">
            {/* Card 1: Your Protection */}
            <div className="relative rounded-[2rem] p-8 text-[#0D3B3B] border border-[#FFD700]/50 bg-[#FFD700] overflow-hidden h-full shadow-xl shadow-[#FFD700]/10" data-id="element-1621">
              {renderProtectionCard()}
            </div>

            {/* Card 2: Wallet Balance */}
            <div onClick={() => navigate('wallet')} className="relative rounded-[2rem] p-8 text-[#0D3B3B] border border-[#FFD700]/50 bg-[#FFD700] overflow-hidden cursor-pointer hover:shadow-xl hover:shadow-[#FFD700]/20 transition-all h-full" data-id="element-1622">
              {renderWalletCard()}
            </div>
          </motion.div> /* MOBILE LAYOUT: Swiping cards */ : <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 24
      }} className="mb-6" data-id="element-1623">
            <div ref={walletScrollRef} onScroll={handleWalletScroll} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-5 px-5" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }} data-id="element-1624">
              <div className="min-w-full snap-center" data-id="element-1625">
                <div className="relative rounded-[2rem] p-6 text-[#0D3B3B] border border-[#FFD700]/50 bg-[#FFD700] overflow-hidden h-full" data-id="element-1626">
                  {renderProtectionCard()}
                </div>
              </div>

              <div className="min-w-full snap-center" data-id="element-1627">
                <div onClick={() => navigate('wallet')} className="relative rounded-[2rem] p-6 text-[#0D3B3B] border border-[#FFD700]/50 bg-[#FFD700] overflow-hidden cursor-pointer active:scale-[0.98] transition-transform h-full" data-id="element-1628">
                  {renderWalletCard()}
                </div>
              </div>
            </div>

            {/* Dot Indicators */}
            <div className="flex justify-center gap-2 mt-3" data-id="element-1629">
              {[0, 1].map(idx => <button key={idx} onClick={() => {
            walletScrollRef.current?.scrollTo({
              left: idx * (walletScrollRef.current?.offsetWidth || 0),
              behavior: 'smooth'
            });
          }} className={`h-1.5 rounded-full transition-all duration-300 ${activeWalletCard === idx ? 'w-6 bg-[#0D3B3B]' : 'w-1.5 bg-[#0D3B3B]/20'}`} data-id="element-1630" />)}
            </div>
          </motion.div>}

        {/* Main Content Grid */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className={viewMode === 'tablet' ? 'grid grid-cols-3 gap-8' : 'space-y-6'} data-id="element-1631">
          {/* My Active Plans */}
          <motion.div variants={itemVariants} className={viewMode === 'tablet' ? 'col-span-2 flex flex-col h-full' : 'flex flex-col h-full'} data-id="element-1632">
            {renderPlansList()}
          </motion.div>

          {/* Quick Actions & Banner */}
          <div className={`flex flex-col ${viewMode === 'tablet' ? 'space-y-8' : 'space-y-6'}`} data-id="element-1633">
            <motion.div variants={itemVariants} data-id="element-1634">
              {renderQuickActions()}
            </motion.div>

            <motion.div variants={itemVariants} className="bg-[#0D3B3B] rounded-[1.5rem] p-5 text-white flex items-center justify-between shadow-lg relative overflow-hidden group cursor-pointer mt-auto" onClick={() => navigate('ai-assistant')} data-id="element-1635">
              {renderAIBanner()}
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Floating AI Assistant Button */}
      <motion.button initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      delay: 0.6,
      type: 'spring',
      damping: 15
    }} onClick={() => navigate('ai-assistant')} className={`absolute right-5 w-14 h-14 bg-[#FFD700] rounded-full shadow-xl shadow-[#FFD700]/40 flex items-center justify-center z-30 active:scale-90 transition-transform border-2 border-white ${viewMode === 'app' ? 'bottom-24' : 'bottom-8'}`} data-id="element-1636">
        <img src="https://cdn.magicpatterns.com/uploads/ddGEqtHHBLaRgK5q4UKEDw/DD_winking_1.png" alt="Skydd mascot" className="w-10 h-10 object-contain drop-shadow-lg" data-id="element-1637" />
      </motion.button>
    </motion.div>;
}